/**
 * feat-302 — the audience chip on a Track header or a full Lesson banner
 * (design NOTES §1, §2, §3).
 *
 * Editable (a button that opens the editor) wherever feat-299 inline editing is
 * live — the English cell in EN and Both. Read-only (a `<span>`) in ES-only
 * mode, where an unscoped item renders nothing. Never on stubs, the "Not in any
 * Track" group, Drills or items.
 *
 * Scope is a fact, not a status: a neutral white pill. Not coral, gold,
 * `preview-*` or `attention-*`, and never the approval pill.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, MapPin, UserRound, Users } from 'lucide-react';
import { Audience } from '../../../../services/quizContentAdminService';
import { FOCUS } from '../InlineEditable';
import { useEditCoordinator } from '../editCoordinator';
import { AUDIENCE_COPY as COPY, AudienceLevel } from './audienceCopy';
import { chipAria, chipSummary, isEveryone, previewSentence } from './audienceFormat';
import { readyOptions, useAudience } from './audienceContext';
import AudienceEditor from './AudienceEditor';

// --- class constants, verbatim from design NOTES §3 -------------------------------
const CHIP_BASE =
  'inline-flex items-center gap-1 max-w-[18rem] h-6 rounded-full text-xs leading-none whitespace-nowrap';
export const CHIP_SCOPED = `${CHIP_BASE} pl-2 pr-1.5 bg-white ring-1 ring-black/10 font-semibold text-ink hover:bg-cream-dark ${FOCUS}`;
export const CHIP_EVERYONE = `${CHIP_BASE} pl-2 pr-1.5 border border-dashed border-black/20 font-medium text-inkMuted hover:bg-cream-dark hover:text-ink ${FOCUS}`;
/** Lesson + unscoped only: hidden at rest from `sm`, revealed on banner hover /
 *  focus-within, always shown on touch and while open. */
export const CHIP_REVEAL =
  'sm:opacity-0 sm:group-hover/banner:opacity-100 sm:group-focus-within/banner:opacity-100 [@media(pointer:coarse)]:opacity-100 aria-expanded:opacity-100';
export const CHIP_READONLY = `${CHIP_BASE} px-2 bg-white/70 ring-1 ring-black/5 font-semibold text-inkMuted`;
const CHIP_ICON = 'w-3 h-3 shrink-0 text-inkMuted';
const SAVED = 'inline-flex items-center gap-1 text-xs leading-none text-inkMuted';

/** How long "✓ Saved" shows after a save (NOTES §6.3). */
export const SAVED_MS = 2000;

/** The chip's label: glyph + segment per scoped dimension, `·` between. */
const ChipLabel: React.FC<{ location: string | null; role: string | null }> = ({
  location,
  role,
}) => (
  <>
    {location && (
      <>
        <MapPin className={CHIP_ICON} aria-hidden />
        <span className="truncate">{location}</span>
      </>
    )}
    {location && role && (
      <span className="text-inkMuted px-0.5" aria-hidden>
        ·
      </span>
    )}
    {role && (
      <>
        <UserRound className={CHIP_ICON} aria-hidden />
        <span className="truncate">{role}</span>
      </>
    )}
  </>
);

export interface AudienceChipProps {
  level: AudienceLevel;
  id: string;
  /** The item's name, shown under the editor's title. */
  name: string;
  audience: Audience;
  editable: boolean;
}

const AudienceChip: React.FC<AudienceChipProps> = ({ level, id, name, audience, editable }) => {
  const api = useAudience();
  const coordinator = useEditCoordinator();
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const chipRef = useRef<HTMLButtonElement | null>(null);
  const tokenRef = useRef<object>({});
  const savedTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (savedTimer.current !== null) window.clearTimeout(savedTimer.current);
      coordinator?.release(tokenRef.current);
    },
    [coordinator],
  );

  /** Esc, outside click, ✕, Cancel, or another field/chip opening: the draft is
   *  discarded. `refocus` is false when focus is moving somewhere on purpose. */
  const close = useCallback(
    (refocus: boolean) => {
      setOpen(false);
      coordinator?.release(tokenRef.current);
      if (refocus) chipRef.current?.focus();
    },
    [coordinator],
  );

  const onOpen = () => {
    if (open) {
      close(true);
      return;
    }
    // feat-299's rule between fields, unchanged: opening this commits whatever
    // inline field (or other audience editor) is open. Another field opening
    // later "commits" this one — for an editor that means discard, the same as
    // clicking outside it.
    coordinator?.claim(tokenRef.current, () => close(false));
    setOpen(true);
  };

  const onSaved = () => {
    close(true);
    setSaved(true);
    if (savedTimer.current !== null) window.clearTimeout(savedTimer.current);
    savedTimer.current = window.setTimeout(() => setSaved(false), SAVED_MS);
  };

  if (!api) return null;
  const options = readyOptions(api.options);
  const summary = chipSummary(audience, options);
  const everyone = isEveryone(audience);

  if (!editable) {
    if (everyone) return null;
    return (
      <span
        className={CHIP_READONLY}
        title={previewSentence(audience, options)}
        data-testid={`audience-chip-${level}-${id}`}
      >
        <ChipLabel location={summary.location} role={summary.role} />
      </span>
    );
  }

  const testId = `audience-chip-${level}-${id}`;
  return (
    <span className="relative inline-flex items-center gap-2">
      {saved && (
        <span className={SAVED} role="status">
          <Check className="w-3 h-3" aria-hidden />
          {COPY.saved}
        </span>
      )}
      <button
        ref={chipRef}
        type="button"
        data-testid={testId}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={chipAria(audience, level, options)}
        title={everyone ? undefined : previewSentence(audience, options)}
        onClick={onOpen}
        className={
          everyone
            ? `${CHIP_EVERYONE}${level === 'lesson' ? ` ${CHIP_REVEAL}` : ''}`
            : CHIP_SCOPED
        }
      >
        {everyone ? (
          <>
            <Users className={CHIP_ICON} aria-hidden />
            <span>{COPY.everyone}</span>
          </>
        ) : (
          <ChipLabel location={summary.location} role={summary.role} />
        )}
        <ChevronDown className={CHIP_ICON} aria-hidden />
      </button>
      {open && (
        <AudienceEditor
          level={level}
          id={id}
          name={name}
          audience={audience}
          anchorRef={chipRef}
          onCancel={() => close(true)}
          onSaved={onSaved}
        />
      )}
    </span>
  );
};

export default AudienceChip;
